// 📋 FAQ com i18n - PersonalNutri.App
// Sistema de FAQ com suporte a múltiplos idiomas

import { useTranslation } from '../hooks/useTranslation';

// Hook para obter dados do FAQ traduzidos
export const useFaqData = () => {
  const { t } = useTranslation();

  // Mapeamento de categorias para tradução
  const categoryMap = {
    'account': 'account',
    'progress': 'progress',
    'water': 'water',
    'goals': 'goals',
    'meals': 'meals',
    'activities': 'activities',
    'routines': 'routines',
    'cycle': 'cycle',
    'medications': 'medications',
    'allergies': 'allergies',
    'ai': 'ai',
    'notifications': 'notifications',
    'subscription': 'subscription',
    'privacy': 'privacy',
    'technical': 'technical',
    'general': 'general'
  };

  // Função para obter dados do FAQ traduzidos
  const getFaqData = () => {
    return [
      {
        categoryKey: 'account',
        title: t('faq.categories.account'),
        icon: 'account-circle',
        questions: [
          {
            key: 'createAccount',
            question: t('faq.questions.account.createAccount.question'),
            answer: t('faq.questions.account.createAccount.answer')
          },
          {
            key: 'changePassword',
            question: t('faq.questions.account.changePassword.question'),
            answer: t('faq.questions.account.changePassword.answer')
          },
          {
            key: 'addPhoto',
            question: t('faq.questions.account.addPhoto.question'),
            answer: t('faq.questions.account.addPhoto.answer')
          },
          {
            key: 'biometric',
            question: t('faq.questions.account.biometric.question'),
            answer: t('faq.questions.account.biometric.answer')
          },
          {
            key: 'deleteAccount',
            question: t('faq.questions.account.deleteAccount.question'),
            answer: t('faq.questions.account.deleteAccount.answer')
          }
        ]
      },
      {
        categoryKey: 'progress',
        title: t('faq.categories.progress'),
        icon: 'chart-line',
        questions: [
          {
            key: 'tmb',
            question: t('faq.questions.progress.tmb.question'),
            answer: t('faq.questions.progress.tmb.answer')
          },
          {
            key: 'get',
            question: t('faq.questions.progress.get.question'),
            answer: t('faq.questions.progress.get.answer')
          },
          {
            key: 'registerWeight',
            question: t('faq.questions.progress.registerWeight.question'),
            answer: t('faq.questions.progress.registerWeight.answer')
          },
          {
            key: 'weightHistory',
            question: t('faq.questions.progress.weightHistory.question'),
            answer: t('faq.questions.progress.weightHistory.answer')
          },
          {
            key: 'bmiCalculation',
            question: t('faq.questions.progress.bmiCalculation.question'),
            answer: t('faq.questions.progress.bmiCalculation.answer')
          }
        ]
      },
      {
        categoryKey: 'water',
        title: t('faq.categories.water'),
        icon: 'water',
        questions: [
          {
            key: 'registerWater',
            question: t('faq.questions.water.registerWater.question'),
            answer: t('faq.questions.water.registerWater.answer')
          },
          {
            key: 'waterHistory',
            question: t('faq.questions.water.waterHistory.question'),
            answer: t('faq.questions.water.waterHistory.answer')
          },
          {
            key: 'waterGoal',
            question: t('faq.questions.water.waterGoal.question'),
            answer: t('faq.questions.water.waterGoal.answer')
          }
        ]
      },
      {
        categoryKey: 'goals',
        title: t('faq.categories.goals'),
        icon: 'flag',
        questions: [
          {
            key: 'createGoal',
            question: t('faq.questions.goals.createGoal.question'),
            answer: t('faq.questions.goals.createGoal.answer')
          },
          {
            key: 'goalTypes',
            question: t('faq.questions.goals.goalTypes.question'),
            answer: t('faq.questions.goals.goalTypes.answer')
          },
          {
            key: 'trackProgress',
            question: t('faq.questions.goals.trackProgress.question'),
            answer: t('faq.questions.goals.trackProgress.answer')
          },
          {
            key: 'multipleGoals',
            question: t('faq.questions.goals.multipleGoals.question'),
            answer: t('faq.questions.goals.multipleGoals.answer')
          }
        ]
      },
      {
        categoryKey: 'meals',
        title: t('faq.categories.meals'),
        icon: 'food',
        questions: [
          {
            key: 'createMealPlan',
            question: t('faq.questions.meals.createMealPlan.question'),
            answer: t('faq.questions.meals.createMealPlan.answer')
          },
          {
            key: 'dailyCalories',
            question: t('faq.questions.meals.dailyCalories.question'),
            answer: t('faq.questions.meals.dailyCalories.answer')
          },
          {
            key: 'quickMeal',
            question: t('faq.questions.meals.quickMeal.question'),
            answer: t('faq.questions.meals.quickMeal.answer')
          },
          {
            key: 'editMeal',
            question: t('faq.questions.meals.editMeal.question'),
            answer: t('faq.questions.meals.editMeal.answer')
          },
          {
            key: 'copyMeal',
            question: t('faq.questions.meals.copyMeal.question'),
            answer: t('faq.questions.meals.copyMeal.answer')
          }
        ]
      },
      {
        categoryKey: 'activities',
        title: t('faq.categories.activities'),
        icon: 'dumbbell',
        questions: [
          {
            key: 'addActivity',
            question: t('faq.questions.activities.addActivity.question'),
            answer: t('faq.questions.activities.addActivity.answer')
          },
          {
            key: 'workoutDivisions',
            question: t('faq.questions.activities.workoutDivisions.question'),
            answer: t('faq.questions.activities.workoutDivisions.answer')
          },
          {
            key: 'customDivision',
            question: t('faq.questions.activities.customDivision.question'),
            answer: t('faq.questions.activities.customDivision.answer')
          },
          {
            key: 'exerciseTimer',
            question: t('faq.questions.activities.exerciseTimer.question'),
            answer: t('faq.questions.activities.exerciseTimer.answer')
          },
          {
            key: 'activitySummary',
            question: t('faq.questions.activities.activitySummary.question'),
            answer: t('faq.questions.activities.activitySummary.answer')
          }
        ]
      },
      {
        categoryKey: 'routines',
        title: t('faq.categories.routines'),
        icon: 'calendar-clock',
        questions: [
          {
            key: 'createRoutine',
            question: t('faq.questions.routines.createRoutine.question'),
            answer: t('faq.questions.routines.createRoutine.answer')
          },
          {
            key: 'activitiesVsRoutines',
            question: t('faq.questions.routines.activitiesVsRoutines.question'),
            answer: t('faq.questions.routines.activitiesVsRoutines.answer')
          },
          {
            key: 'routineNotifications',
            question: t('faq.questions.routines.routineNotifications.question'),
            answer: t('faq.questions.routines.routineNotifications.answer')
          }
        ]
      },
      {
        categoryKey: 'cycle',
        title: t('faq.categories.cycle'),
        icon: 'calendar-heart',
        questions: [
          {
            key: 'startCycle',
            question: t('faq.questions.cycle.startCycle.question'),
            answer: t('faq.questions.cycle.startCycle.answer')
          },
          {
            key: 'configureCycle',
            question: t('faq.questions.cycle.configureCycle.question'),
            answer: t('faq.questions.cycle.configureCycle.answer')
          },
          {
            key: 'workoutTips',
            question: t('faq.questions.cycle.workoutTips.question'),
            answer: t('faq.questions.cycle.workoutTips.answer')
          },
          {
            key: 'cycleCalendar',
            question: t('faq.questions.cycle.cycleCalendar.question'),
            answer: t('faq.questions.cycle.cycleCalendar.answer')
          },
          {
            key: 'endCycle',
            question: t('faq.questions.cycle.endCycle.question'),
            answer: t('faq.questions.cycle.endCycle.answer')
          }
        ]
      },
      {
        categoryKey: 'medications',
        title: t('faq.categories.medications'),
        icon: 'medical-bag',
        questions: [
          {
            key: 'addMedication',
            question: t('faq.questions.medications.addMedication.question'),
            answer: t('faq.questions.medications.addMedication.answer')
          },
          {
            key: 'customFormulas',
            question: t('faq.questions.medications.customFormulas.question'),
            answer: t('faq.questions.medications.customFormulas.answer')
          },
          {
            key: 'medicationReminders',
            question: t('faq.questions.medications.medicationReminders.question'),
            answer: t('faq.questions.medications.medicationReminders.answer')
          }
        ]
      },
      {
        categoryKey: 'allergies',
        title: t('faq.categories.allergies'),
        icon: 'alert-circle',
        questions: [
          {
            key: 'addAllergies',
            question: t('faq.questions.allergies.addAllergies.question'),
            answer: t('faq.questions.allergies.addAllergies.answer')
          },
          {
            key: 'foodRestrictions',
            question: t('faq.questions.allergies.foodRestrictions.question'),
            answer: t('faq.questions.allergies.foodRestrictions.answer')
          },
          {
            key: 'preferences',
            question: t('faq.questions.allergies.preferences.question'),
            answer: t('faq.questions.allergies.preferences.answer')
          }
        ]
      },
      {
        categoryKey: 'ai',
        title: t('faq.categories.ai'),
        icon: 'robot',
        questions: [
          {
            key: 'chatWithAI',
            question: t('faq.questions.ai.chatWithAI.question'),
            answer: t('faq.questions.ai.chatWithAI.answer')
          },
          {
            key: 'aiDataAccess',
            question: t('faq.questions.ai.aiDataAccess.question'),
            answer: t('faq.questions.ai.aiDataAccess.answer')
          },
          {
            key: 'conversationHistory',
            question: t('faq.questions.ai.conversationHistory.question'),
            answer: t('faq.questions.ai.conversationHistory.answer')
          },
          {
            key: 'aiVsNutritionist',
            question: t('faq.questions.ai.aiVsNutritionist.question'),
            answer: t('faq.questions.ai.aiVsNutritionist.answer')
          }
        ]
      },
      {
        categoryKey: 'notifications',
        title: t('faq.categories.notifications'),
        icon: 'bell',
        questions: [
          {
            key: 'activateNotifications',
            question: t('faq.questions.notifications.activateNotifications.question'),
            answer: t('faq.questions.notifications.activateNotifications.answer')
          },
          {
            key: 'noNotifications',
            question: t('faq.questions.notifications.noNotifications.question'),
            answer: t('faq.questions.notifications.noNotifications.answer')
          },
          {
            key: 'customizeTimes',
            question: t('faq.questions.notifications.customizeTimes.question'),
            answer: t('faq.questions.notifications.customizeTimes.answer')
          }
        ]
      },
      {
        categoryKey: 'subscription',
        title: t('faq.categories.subscription'),
        icon: 'credit-card',
        questions: [
          {
            key: 'availablePlans',
            question: t('faq.questions.subscription.availablePlans.question'),
            answer: t('faq.questions.subscription.availablePlans.answer')
          },
          {
            key: 'subscribePremium',
            question: t('faq.questions.subscription.subscribePremium.question'),
            answer: t('faq.questions.subscription.subscribePremium.answer')
          },
          {
            key: 'cancelSubscription',
            question: t('faq.questions.subscription.cancelSubscription.question'),
            answer: t('faq.questions.subscription.cancelSubscription.answer')
          },
          {
            key: 'viewInvoices',
            question: t('faq.questions.subscription.viewInvoices.question'),
            answer: t('faq.questions.subscription.viewInvoices.answer')
          },
          {
            key: 'refund',
            question: t('faq.questions.subscription.refund.question'),
            answer: t('faq.questions.subscription.refund.answer')
          }
        ]
      },
      {
        categoryKey: 'privacy',
        title: t('faq.categories.privacy'),
        icon: 'shield-check',
        questions: [
          {
            key: 'dataSecurity',
            question: t('faq.questions.privacy.dataSecurity.question'),
            answer: t('faq.questions.privacy.dataSecurity.answer')
          },
          {
            key: 'exportData',
            question: t('faq.questions.privacy.exportData.question'),
            answer: t('faq.questions.privacy.exportData.answer')
          },
          {
            key: 'deleteAccount',
            question: t('faq.questions.privacy.deleteAccount.question'),
            answer: t('faq.questions.privacy.deleteAccount.answer')
          }
        ]
      },
      {
        categoryKey: 'technical',
        title: t('faq.categories.technical'),
        icon: 'cog',
        questions: [
          {
            key: 'appCrashing',
            question: t('faq.questions.technical.appCrashing.question'),
            answer: t('faq.questions.technical.appCrashing.answer')
          },
          {
            key: 'lostData',
            question: t('faq.questions.technical.lostData.question'),
            answer: t('faq.questions.technical.lostData.answer')
          },
          {
            key: 'syncIssues',
            question: t('faq.questions.technical.syncIssues.question'),
            answer: t('faq.questions.technical.syncIssues.answer')
          },
          {
            key: 'reportBug',
            question: t('faq.questions.technical.reportBug.question'),
            answer: t('faq.questions.technical.reportBug.answer')
          }
        ]
      },
      {
        categoryKey: 'general',
        title: t('faq.categories.general'),
        icon: 'help-circle',
        questions: [
          {
            key: 'offlineMode',
            question: t('faq.questions.general.offlineMode.question'),
            answer: t('faq.questions.general.offlineMode.answer')
          },
          {
            key: 'multipleDevices',
            question: t('faq.questions.general.multipleDevices.question'),
            answer: t('faq.questions.general.multipleDevices.answer')
          },
          {
            key: 'updateApp',
            question: t('faq.questions.general.updateApp.question'),
            answer: t('faq.questions.general.updateApp.answer')
          },
          {
            key: 'changelog',
            question: t('faq.questions.general.changelog.question'),
            answer: t('faq.questions.general.changelog.answer')
          },
          {
            key: 'getHelp',
            question: t('faq.questions.general.getHelp.question'),
            answer: t('faq.questions.general.getHelp.answer')
          }
        ]
      }
    ];
  };

  return {
    faqData: getFaqData(),
    categoryMap
  };
};

// Função para obter dados do FAQ sem hook (para uso em componentes que não podem usar hooks)
export const getFaqDataStatic = (t) => {
  return [
    {
      categoryKey: 'account',
      title: t('faq.categories.account'),
      icon: 'account-circle',
      questions: [
        {
          key: 'createAccount',
          question: t('faq.questions.account.createAccount.question'),
          answer: t('faq.questions.account.createAccount.answer')
        },
        {
          key: 'changePassword',
          question: t('faq.questions.account.changePassword.question'),
          answer: t('faq.questions.account.changePassword.answer')
        },
        {
          key: 'addPhoto',
          question: t('faq.questions.account.addPhoto.question'),
          answer: t('faq.questions.account.addPhoto.answer')
        },
        {
          key: 'biometric',
          question: t('faq.questions.account.biometric.question'),
          answer: t('faq.questions.account.biometric.answer')
        },
        {
          key: 'deleteAccount',
          question: t('faq.questions.account.deleteAccount.question'),
          answer: t('faq.questions.account.deleteAccount.answer')
        }
      ]
    },
    {
      categoryKey: 'progress',
      title: t('faq.categories.progress'),
      icon: 'chart-line',
      questions: [
        {
          key: 'tmb',
          question: t('faq.questions.progress.tmb.question'),
          answer: t('faq.questions.progress.tmb.answer')
        },
        {
          key: 'get',
          question: t('faq.questions.progress.get.question'),
          answer: t('faq.questions.progress.get.answer')
        },
        {
          key: 'registerWeight',
          question: t('faq.questions.progress.registerWeight.question'),
          answer: t('faq.questions.progress.registerWeight.answer')
        },
        {
          key: 'weightHistory',
          question: t('faq.questions.progress.weightHistory.question'),
          answer: t('faq.questions.progress.weightHistory.answer')
        },
        {
          key: 'bmiCalculation',
          question: t('faq.questions.progress.bmiCalculation.question'),
          answer: t('faq.questions.progress.bmiCalculation.answer')
        }
      ]
    },
    {
      categoryKey: 'water',
      title: t('faq.categories.water'),
      icon: 'water',
      questions: [
        {
          key: 'registerWater',
          question: t('faq.questions.water.registerWater.question'),
          answer: t('faq.questions.water.registerWater.answer')
        },
        {
          key: 'waterHistory',
          question: t('faq.questions.water.waterHistory.question'),
          answer: t('faq.questions.water.waterHistory.answer')
        },
        {
          key: 'waterGoal',
          question: t('faq.questions.water.waterGoal.question'),
          answer: t('faq.questions.water.waterGoal.answer')
        }
      ]
    },
    {
      categoryKey: 'goals',
      title: t('faq.categories.goals'),
      icon: 'flag',
      questions: [
        {
          key: 'createGoal',
          question: t('faq.questions.goals.createGoal.question'),
          answer: t('faq.questions.goals.createGoal.answer')
        },
        {
          key: 'goalTypes',
          question: t('faq.questions.goals.goalTypes.question'),
          answer: t('faq.questions.goals.goalTypes.answer')
        },
        {
          key: 'trackProgress',
          question: t('faq.questions.goals.trackProgress.question'),
          answer: t('faq.questions.goals.trackProgress.answer')
        },
        {
          key: 'multipleGoals',
          question: t('faq.questions.goals.multipleGoals.question'),
          answer: t('faq.questions.goals.multipleGoals.answer')
        }
      ]
    },
    {
      categoryKey: 'meals',
      title: t('faq.categories.meals'),
      icon: 'food',
      questions: [
        {
          key: 'createMealPlan',
          question: t('faq.questions.meals.createMealPlan.question'),
          answer: t('faq.questions.meals.createMealPlan.answer')
        },
        {
          key: 'dailyCalories',
          question: t('faq.questions.meals.dailyCalories.question'),
          answer: t('faq.questions.meals.dailyCalories.answer')
        },
        {
          key: 'quickMeal',
          question: t('faq.questions.meals.quickMeal.question'),
          answer: t('faq.questions.meals.quickMeal.answer')
        },
        {
          key: 'editMeal',
          question: t('faq.questions.meals.editMeal.question'),
          answer: t('faq.questions.meals.editMeal.answer')
        },
        {
          key: 'copyMeal',
          question: t('faq.questions.meals.copyMeal.question'),
          answer: t('faq.questions.meals.copyMeal.answer')
        }
      ]
    },
    {
      categoryKey: 'activities',
      title: t('faq.categories.activities'),
      icon: 'dumbbell',
      questions: [
        {
          key: 'addActivity',
          question: t('faq.questions.activities.addActivity.question'),
          answer: t('faq.questions.activities.addActivity.answer')
        },
        {
          key: 'workoutDivisions',
          question: t('faq.questions.activities.workoutDivisions.question'),
          answer: t('faq.questions.activities.workoutDivisions.answer')
        },
        {
          key: 'customDivision',
          question: t('faq.questions.activities.customDivision.question'),
          answer: t('faq.questions.activities.customDivision.answer')
        },
        {
          key: 'exerciseTimer',
          question: t('faq.questions.activities.exerciseTimer.question'),
          answer: t('faq.questions.activities.exerciseTimer.answer')
        },
        {
          key: 'activitySummary',
          question: t('faq.questions.activities.activitySummary.question'),
          answer: t('faq.questions.activities.activitySummary.answer')
        }
      ]
    },
    {
      categoryKey: 'routines',
      title: t('faq.categories.routines'),
      icon: 'calendar-clock',
      questions: [
        {
          key: 'createRoutine',
          question: t('faq.questions.routines.createRoutine.question'),
          answer: t('faq.questions.routines.createRoutine.answer')
        },
        {
          key: 'activitiesVsRoutines',
          question: t('faq.questions.routines.activitiesVsRoutines.question'),
          answer: t('faq.questions.routines.activitiesVsRoutines.answer')
        },
        {
          key: 'routineNotifications',
          question: t('faq.questions.routines.routineNotifications.question'),
          answer: t('faq.questions.routines.routineNotifications.answer')
        }
      ]
    },
    {
      categoryKey: 'cycle',
      title: t('faq.categories.cycle'),
      icon: 'calendar-heart',
      questions: [
        {
          key: 'startCycle',
          question: t('faq.questions.cycle.startCycle.question'),
          answer: t('faq.questions.cycle.startCycle.answer')
        },
        {
          key: 'configureCycle',
          question: t('faq.questions.cycle.configureCycle.question'),
          answer: t('faq.questions.cycle.configureCycle.answer')
        },
        {
          key: 'workoutTips',
          question: t('faq.questions.cycle.workoutTips.question'),
          answer: t('faq.questions.cycle.workoutTips.answer')
        },
        {
          key: 'cycleCalendar',
          question: t('faq.questions.cycle.cycleCalendar.question'),
          answer: t('faq.questions.cycle.cycleCalendar.answer')
        },
        {
          key: 'endCycle',
          question: t('faq.questions.cycle.endCycle.question'),
          answer: t('faq.questions.cycle.endCycle.answer')
        }
      ]
    },
    {
      categoryKey: 'medications',
      title: t('faq.categories.medications'),
      icon: 'medical-bag',
      questions: [
        {
          key: 'addMedication',
          question: t('faq.questions.medications.addMedication.question'),
          answer: t('faq.questions.medications.addMedication.answer')
        },
        {
          key: 'customFormulas',
          question: t('faq.questions.medications.customFormulas.question'),
          answer: t('faq.questions.medications.customFormulas.answer')
        },
        {
          key: 'medicationReminders',
          question: t('faq.questions.medications.medicationReminders.question'),
          answer: t('faq.questions.medications.medicationReminders.answer')
        }
      ]
    },
    {
      categoryKey: 'allergies',
      title: t('faq.categories.allergies'),
      icon: 'alert-circle',
      questions: [
        {
          key: 'addAllergies',
          question: t('faq.questions.allergies.addAllergies.question'),
          answer: t('faq.questions.allergies.addAllergies.answer')
        },
        {
          key: 'foodRestrictions',
          question: t('faq.questions.allergies.foodRestrictions.question'),
          answer: t('faq.questions.allergies.foodRestrictions.answer')
        },
        {
          key: 'preferences',
          question: t('faq.questions.allergies.preferences.question'),
          answer: t('faq.questions.allergies.preferences.answer')
        }
      ]
    },
    {
      categoryKey: 'ai',
      title: t('faq.categories.ai'),
      icon: 'robot',
      questions: [
        {
          key: 'chatWithAI',
          question: t('faq.questions.ai.chatWithAI.question'),
          answer: t('faq.questions.ai.chatWithAI.answer')
        },
        {
          key: 'aiDataAccess',
          question: t('faq.questions.ai.aiDataAccess.question'),
          answer: t('faq.questions.ai.aiDataAccess.answer')
        },
        {
          key: 'conversationHistory',
          question: t('faq.questions.ai.conversationHistory.question'),
          answer: t('faq.questions.ai.conversationHistory.answer')
        },
        {
          key: 'aiVsNutritionist',
          question: t('faq.questions.ai.aiVsNutritionist.question'),
          answer: t('faq.questions.ai.aiVsNutritionist.answer')
        }
      ]
    },
    {
      categoryKey: 'notifications',
      title: t('faq.categories.notifications'),
      icon: 'bell',
      questions: [
        {
          key: 'activateNotifications',
          question: t('faq.questions.notifications.activateNotifications.question'),
          answer: t('faq.questions.notifications.activateNotifications.answer')
        },
        {
          key: 'noNotifications',
          question: t('faq.questions.notifications.noNotifications.question'),
          answer: t('faq.questions.notifications.noNotifications.answer')
        },
        {
          key: 'customizeTimes',
          question: t('faq.questions.notifications.customizeTimes.question'),
          answer: t('faq.questions.notifications.customizeTimes.answer')
        }
      ]
    },
    {
      categoryKey: 'subscription',
      title: t('faq.categories.subscription'),
      icon: 'credit-card',
      questions: [
        {
          key: 'availablePlans',
          question: t('faq.questions.subscription.availablePlans.question'),
          answer: t('faq.questions.subscription.availablePlans.answer')
        },
        {
          key: 'subscribePremium',
          question: t('faq.questions.subscription.subscribePremium.question'),
          answer: t('faq.questions.subscription.subscribePremium.answer')
        },
        {
          key: 'cancelSubscription',
          question: t('faq.questions.subscription.cancelSubscription.question'),
          answer: t('faq.questions.subscription.cancelSubscription.answer')
        },
        {
          key: 'viewInvoices',
          question: t('faq.questions.subscription.viewInvoices.question'),
          answer: t('faq.questions.subscription.viewInvoices.answer')
        },
        {
          key: 'refund',
          question: t('faq.questions.subscription.refund.question'),
          answer: t('faq.questions.subscription.refund.answer')
        }
      ]
    },
    {
      categoryKey: 'privacy',
      title: t('faq.categories.privacy'),
      icon: 'shield-check',
      questions: [
        {
          key: 'dataSecurity',
          question: t('faq.questions.privacy.dataSecurity.question'),
          answer: t('faq.questions.privacy.dataSecurity.answer')
        },
        {
          key: 'exportData',
          question: t('faq.questions.privacy.exportData.question'),
          answer: t('faq.questions.privacy.exportData.answer')
        },
        {
          key: 'deleteAccount',
          question: t('faq.questions.privacy.deleteAccount.question'),
          answer: t('faq.questions.privacy.deleteAccount.answer')
        }
      ]
    },
    {
      categoryKey: 'technical',
      title: t('faq.categories.technical'),
      icon: 'cog',
      questions: [
        {
          key: 'appCrashing',
          question: t('faq.questions.technical.appCrashing.question'),
          answer: t('faq.questions.technical.appCrashing.answer')
        },
        {
          key: 'lostData',
          question: t('faq.questions.technical.lostData.question'),
          answer: t('faq.questions.technical.lostData.answer')
        },
        {
          key: 'syncIssues',
          question: t('faq.questions.technical.syncIssues.question'),
          answer: t('faq.questions.technical.syncIssues.answer')
        },
        {
          key: 'reportBug',
          question: t('faq.questions.technical.reportBug.question'),
          answer: t('faq.questions.technical.reportBug.answer')
        }
      ]
    },
    {
      categoryKey: 'general',
      title: t('faq.categories.general'),
      icon: 'help-circle',
      questions: [
        {
          key: 'offlineMode',
          question: t('faq.questions.general.offlineMode.question'),
          answer: t('faq.questions.general.offlineMode.answer')
        },
        {
          key: 'multipleDevices',
          question: t('faq.questions.general.multipleDevices.question'),
          answer: t('faq.questions.general.multipleDevices.answer')
        },
        {
          key: 'updateApp',
          question: t('faq.questions.general.updateApp.question'),
          answer: t('faq.questions.general.updateApp.answer')
        },
        {
          key: 'changelog',
          question: t('faq.questions.general.changelog.question'),
          answer: t('faq.questions.general.changelog.answer')
        },
        {
          key: 'getHelp',
          question: t('faq.questions.general.getHelp.question'),
          answer: t('faq.questions.general.getHelp.answer')
        }
      ]
    }
  ];
};
